// FeedComponent.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import UniversalComponent from '../component/UniversalComponent.jsx';
import WithIntersectionObserver from '../component/WithIntersectionObserver.jsx';

import {
  HomeCategoryUi,
  CategoryLayout,
  CategoryComponentUi,
  PSEUDO_TRENDING_HASHTAG,
  PSEUDO_TRENDING_CATEGORY,
  PSEUDO_LEADERBOARD,
  PSEUDO_SHOWCASE,
  FeedCardSize,
} from '../resource/feedConstants.js';
import getTranslationKey from '../resource/getTranslationKey.js';

// Constants for placeholder heights (estimated values for lazy load)
// FeedCardSize: MESSAGE_CARD_HEIGHT=172, SHORTS_CARD_HEIGHT=240, PROFILE_CARD_HEIGHT=296.42
const HEADER_HEIGHT = 34; // FeedMessages.jsx Header: Title(30px) + margin-bottom(4px)
const { MESSAGE_CARD_HEIGHT, SHORTS_CARD_HEIGHT, PROFILE_CARD_HEIGHT } =
  FeedCardSize;
const BANNER_HEIGHT = 136; // HomeBanner.jsx min-height: 136px
const BANNER_WITH_GAP_HEIGHT = BANNER_HEIGHT + 16;
const FLIX_ROW_HEIGHT = 340; // DiscoverFlixRow.jsx: desktop 372px, tablet 274px, mobile 360px
const FLIX_GRID_HEIGHT = 800;
const FLIX_LIST_HEIGHT = 500;
const LIVESTREAM_ROW_HEIGHT = 262; // HomeLiveStreamCategory.jsx: calc(302px - SCROLL_BAR_HEIGHT)
const LIVESTREAM_GRID_HEIGHT = 400;
const USER_CATEGORY_HEIGHT = 140; // UserCategory.jsx: avatar(80px) + name + padding
const HASHTAG_CATEGORY_HEIGHT = 60; // HashtagCategoryItem: 36px(desktop)/28px(mobile) + padding
const SHOWCASE_HEIGHT = 260; // variables.js: mobile 240px, tablet 300px, desktop 522px
const LEADERBOARD_HEIGHT = 350;
const POST_FEED_HEIGHT = 500;

// Pseudo category height mapping
const PSEUDO_HEIGHT_PATTERNS = [
  { pattern: /^pseudo_(video_)?banner$/, height: BANNER_WITH_GAP_HEIGHT },
  {
    pattern: new RegExp(
      `^(${PSEUDO_TRENDING_CATEGORY}|${PSEUDO_TRENDING_HASHTAG})$`
    ),
    height: HASHTAG_CATEGORY_HEIGHT,
  },
  {
    pattern: new RegExp(`^${PSEUDO_LEADERBOARD}$`),
    height: LEADERBOARD_HEIGHT,
  },
  { pattern: new RegExp(`^${PSEUDO_SHOWCASE}$`), height: SHOWCASE_HEIGHT },
  {
    pattern: /^pseudo_following_stories/i,
    height: HEADER_HEIGHT + MESSAGE_CARD_HEIGHT,
  },
  {
    pattern: /^pseudo_following/i,
    height: HEADER_HEIGHT + USER_CATEGORY_HEIGHT,
  },
];

// UI type to height mapping
const USER_HEIGHT = HEADER_HEIGHT + USER_CATEGORY_HEIGHT;
const UI_HEIGHT_MAP = {
  [HomeCategoryUi.FLIX]: FLIX_ROW_HEIGHT,
  [HomeCategoryUi.FLIX_LIST]: FLIX_LIST_HEIGHT,
  [HomeCategoryUi.FLIX_GRID]: FLIX_GRID_HEIGHT,
  [HomeCategoryUi.FLIX_GRID_MOBILE_FLIX_LIST]: FLIX_GRID_HEIGHT,
  [HomeCategoryUi.LIVESTREAM]: LIVESTREAM_ROW_HEIGHT,
  [HomeCategoryUi.LIVESTREAM_GRID]: LIVESTREAM_GRID_HEIGHT,
  [HomeCategoryUi.LIVESTREAM_SQUARE]: LIVESTREAM_GRID_HEIGHT,
  [HomeCategoryUi.LIVESTREAM_TOP]: LIVESTREAM_GRID_HEIGHT,
  [HomeCategoryUi.USER]: USER_HEIGHT,
  [HomeCategoryUi.USER_GRID]: USER_HEIGHT,
  [HomeCategoryUi.USER_SQUARE]: USER_HEIGHT,
  [HomeCategoryUi.USER_SQUARE_DOUBLE_ROW]: USER_HEIGHT,
  [HomeCategoryUi.USER_SQUARE_GRID]: USER_HEIGHT,
  [HomeCategoryUi.LEADERBOARD_USER_SQUARE]: USER_HEIGHT,
  [HomeCategoryUi.LEADERBOARD_USER_SQUARE_DOUBLE_ROW]: USER_HEIGHT,
  [HomeCategoryUi.LEADERBOARD_USER_SQUARE_GRID]: USER_HEIGHT,
  [HomeCategoryUi.SHORTS]: HEADER_HEIGHT + SHORTS_CARD_HEIGHT,
  [HomeCategoryUi.SHORTS_METADATA_CARD]: HEADER_HEIGHT + PROFILE_CARD_HEIGHT,
  [HomeCategoryUi.SHORTS_METADATA_CARD_GRID]: PROFILE_CARD_HEIGHT * 2,
  [HomeCategoryUi.POST_FEED]: POST_FEED_HEIGHT,
  [HomeCategoryUi.POST_CARD_GRID]: POST_FEED_HEIGHT,
  [HomeCategoryUi.STORY_METADATA_CARD]: HEADER_HEIGHT + PROFILE_CARD_HEIGHT,
  [HomeCategoryUi.STORY_METADATA_CARD_GRID]: PROFILE_CARD_HEIGHT * 2,
  [HomeCategoryUi.STORY]: HEADER_HEIGHT + MESSAGE_CARD_HEIGHT,
  [HomeCategoryUi.STORY_GRID]: MESSAGE_CARD_HEIGHT * 3,
  [HomeCategoryUi.STORY_LIST]: FLIX_LIST_HEIGHT,
  [HomeCategoryUi.MESSAGE_LIST]: FLIX_LIST_HEIGHT,
};

const DEFAULT_HEIGHT = HEADER_HEIGHT + MESSAGE_CARD_HEIGHT;

/**
 * Get placeholder height based on UI type.
 * @param {string} ui - HomeCategoryUi type
 * @param {string} category - category string
 * @returns {number} placeholder height (px)
 */
export const getPlaceholderHeight = (ui, category) => {
  // Handle pseudo categories
  if (/^pseudo_/i.test(category)) {
    const matched = PSEUDO_HEIGHT_PATTERNS.find(({ pattern }) =>
      pattern.test(category)
    );
    return matched?.height ?? 200;
  }

  // Handle normal feed types
  return UI_HEIGHT_MAP[ui] ?? DEFAULT_HEIGHT;
};

export const FeedPlaceholder = styled.div`
  height: ${({ height }) => height}px;
  background: transparent;
`;

/**
 * @typedef ParsedCategory
 * @property {string} ui - indicate which ui component should use.
 * @property {string} category - category string.
 */

/**
 * Parse category string.
 * @param {string} {category} - category string.
 * @param {boolean} {isLivestreamFeed} - indicate category is livestream feed or not.
 * @param {boolean} {isLastCategory} - indicate category is last item in categories.
 * @return {ParsedCategory}
 */
export const parseCategoryString = ({
  category,
  isLivestreamFeed,
  isLastCategory,
} = {}) => {
  const url = new URL(category, 'https://swag.live');
  let ui = url.searchParams.get('ui') || url.searchParams.get('_ui');
  if (!ui) {
    const isPseudo = /^pseudo_/i.test(category);
    if (isPseudo) {
      ui = undefined;
    } else if (isLastCategory) {
      if (/^(flix|posts)_/.test(category)) {
        ui = HomeCategoryUi.FLIX_GRID;
      } else if (/^livestream_|^user_/.test(category)) {
        ui = HomeCategoryUi.LIVESTREAM_GRID;
      } else {
        ui = HomeCategoryUi.STORY_GRID;
      }
    } else if (/^livestream_/.test(category) || isLivestreamFeed) {
      ui = HomeCategoryUi.LIVESTREAM;
    } else if (/^post_image_/.test(category)) {
      ui = HomeCategoryUi.POST_CARD_GRID;
    } else if (/^(flix|posts|post_video)_/.test(category)) {
      ui = HomeCategoryUi.FLIX;
    } else if (/^story_|stories_|^home$/.test(category)) {
      ui = HomeCategoryUi.STORY;
    } else if (/^short_by_category_/.test(category)) {
      ui = HomeCategoryUi.SHORTS_METADATA_CARD;
    } else if (/^shorts_/.test(category)) {
      ui = HomeCategoryUi.SHORTS;
    } else if (/^user/.test(category)) {
      ui = HomeCategoryUi.USER;
    } else {
      ui = HomeCategoryUi.STORY;
    }
  }
  return {
    category: decodeURIComponent(
      `${url.pathname.replace(/^\//, '')}${url.search}`
    ),
    ui,
  };
};

/**
 * Get user category component's props.
 * @param {HomeCategoryUi} {ui} - category ui.
 * @return {object} Return component props.
 */
const getUserCategoryProps = ({ ui } = {}) => {
  const props = {};
  switch (ui) {
    case HomeCategoryUi.USER_GRID: {
      props.withHalo = true;
      props.isGrid = true;
      break;
    }
    case HomeCategoryUi.USER_SQUARE: {
      props.isSquare = true;
      break;
    }
    case HomeCategoryUi.USER_SQUARE_DOUBLE_ROW: {
      props.isSquare = true;
      props.isDoubleRow = true;
      break;
    }
    case HomeCategoryUi.USER_SQUARE_GRID: {
      props.isSquare = true;
      props.isGrid = true;
      break;
    }
    case HomeCategoryUi.LEADERBOARD_USER_SQUARE: {
      props.isSquare = true;
      props.isLeaderboard = true;
      break;
    }
    case HomeCategoryUi.LEADERBOARD_USER_SQUARE_DOUBLE_ROW: {
      props.isSquare = true;
      props.isLeaderboard = true;
      props.isDoubleRow = true;
      break;
    }
    case HomeCategoryUi.LEADERBOARD_USER_SQUARE_GRID: {
      props.isSquare = true;
      props.isLeaderboard = true;
      props.isGrid = true;
      break;
    }
    case HomeCategoryUi.USER:
    default: {
      props.withHalo = true;
      break;
    }
  }
  return props;
};

/**
 * get feed component props (not include lazy load logic)
 * @param {object} options
 * @returns {object|null} componentProps or null if invalid
 */
const getFeedComponentProps = ({
  category,
  livestreamFeed,
  titleI18nId,
  isLastCategory,
}) => {
  const componentProps = {};
  const parsed = parseCategoryString({
    category,
    isLivestreamFeed: category === livestreamFeed,
    isLastCategory,
  });
  componentProps.category = parsed.category;
  componentProps.titleI18nId =
    titleI18nId || getTranslationKey({ key: category });
  componentProps.ui = parsed.ui;

  const isPseudo = /^pseudo_/i.test(category);
  if (isPseudo) {
    switch (true) {
      case 'pseudo_banner' === category:
      case 'pseudo_video_banner' === category: {
        componentProps.filename = 'Container_HomeBanner';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HomeBanner.js');
        break;
      }
      case [PSEUDO_TRENDING_CATEGORY, PSEUDO_TRENDING_HASHTAG].includes(
        category
      ): {
        componentProps.filename = 'Container_HashtagCategoryList';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HashtagCategoryList.js');
        break;
      }
      case PSEUDO_LEADERBOARD === category: {
        componentProps.filename = 'Container_HomeLeaderboard';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HomeLeaderboard.js');
        break;
      }
      case PSEUDO_SHOWCASE === category: {
        componentProps.filename = 'Container_HomeShowcaseList';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HomeShowcaseList.js');
        componentProps.shouldUseLazyImage = false;
        break;
      }
      case 'pseudo_livestream_showcase' === category: {
        componentProps.filename = 'Container_LivestreamShowcaseList';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/LivestreamShowcaseList.js');
        break;
      }
      case 'pseudo_freezone_button' === category: {
        componentProps.filename = 'Component_FreezoneLink';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../component/FreezoneLink.jsx');
        break;
      }
      case 'pseudo_livestream_button' === category: {
        componentProps.filename = 'Component_LivestreamLink';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../component/LivestreamLink.jsx');
        break;
      }
      case /^pseudo_following_stories/i.test(category): {
        componentProps.filename = 'Container_FollowingStoryRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FollowingStoryRow.js');
        componentProps.shouldShowTitle = true;
        componentProps.shouldShowMoreLink = true;
        componentProps.layout = CategoryLayout.ROW;
        componentProps.componentUi = CategoryComponentUi.MESSAGE_CARD;
        break;
      }
      case /^pseudo_following/i.test(category): {
        componentProps.filename = 'Container_UserCategory';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/UserCategory.js');
        componentProps.isFollowing = true;
        const categoryProps = getUserCategoryProps({ ui: parsed.ui });
        Object.keys(categoryProps).forEach(
          key => (componentProps[key] = categoryProps[key])
        );
        break;
      }
      default: {
        return null;
      }
    }
  } else {
    switch (parsed.ui) {
      case HomeCategoryUi.FLIX_GRID: {
        componentProps.filename = 'Container_DiscoverFlixMoreRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/DiscoverFlixMoreRow.js');
        break;
      }
      case HomeCategoryUi.FLIX_GRID_MOBILE_FLIX_LIST: {
        componentProps.filename = 'Container_DiscoverFlixMoreRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/DiscoverFlixMoreRow.js');
        componentProps.shouldShowListLayoutOnMobile = true;
        break;
      }
      case HomeCategoryUi.FLIX: {
        componentProps.filename = 'Container_DiscoverFlixRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/DiscoverFlixRow.js');
        break;
      }
      case HomeCategoryUi.FLIX_LIST: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.layout = CategoryLayout.LIST;
        componentProps.componentUi = CategoryComponentUi.FLIX_LIST_ITEM;
        break;
      }
      case HomeCategoryUi.LIVESTREAM_GRID:
      case HomeCategoryUi.LIVESTREAM_SQUARE: {
        componentProps.filename = 'Container_HomeLiveStreamFeed';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HomeLiveStreamFeed.js');
        componentProps.isSquare =
          HomeCategoryUi.LIVESTREAM_SQUARE === parsed.ui;
        break;
      }
      case HomeCategoryUi.LIVESTREAM_TOP: {
        componentProps.filename = 'Container_TopHomeLiveStreamFeed';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/TopHomeLiveStreamFeed.js');
        break;
      }
      case HomeCategoryUi.LIVESTREAM: {
        componentProps.filename = 'Container_HomeLiveStreamCategory';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/HomeLiveStreamCategory.js');
        break;
      }
      case HomeCategoryUi.USER:
      case HomeCategoryUi.USER_GRID:
      case HomeCategoryUi.USER_SQUARE:
      case HomeCategoryUi.USER_SQUARE_DOUBLE_ROW:
      case HomeCategoryUi.USER_SQUARE_GRID:
      case HomeCategoryUi.LEADERBOARD_USER_SQUARE:
      case HomeCategoryUi.LEADERBOARD_USER_SQUARE_DOUBLE_ROW:
      case HomeCategoryUi.LEADERBOARD_USER_SQUARE_GRID: {
        componentProps.filename = 'Container_UserCategory';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/UserCategory.js');
        const categoryProps = getUserCategoryProps({ ui: parsed.ui });
        Object.keys(categoryProps).forEach(
          key => (componentProps[key] = categoryProps[key])
        );
        break;
      }
      case HomeCategoryUi.SHORTS: {
        componentProps.filename = 'Container_ShortsRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/ShortsRow.js');
        componentProps.shouldShowTitle = true;
        componentProps.shouldShowMoreLink = true;
        componentProps.layout = CategoryLayout.ROW;
        componentProps.componentUi = CategoryComponentUi.MESSAGE_CARD;
        break;
      }
      case HomeCategoryUi.SHORTS_METADATA_CARD: {
        componentProps.filename = 'Container_ShortsRow';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/ShortsRow.js');
        componentProps.shouldShowTitle = true;
        componentProps.shouldShowMoreLink = true;
        componentProps.layout = CategoryLayout.ROW;
        componentProps.componentUi = CategoryComponentUi.PROFILE_CARD_ITEM;
        break;
      }
      case HomeCategoryUi.SHORTS_METADATA_CARD_GRID: {
        componentProps.filename = 'Container_ShortsGrid';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/ShortsGrid.js');
        componentProps.layout = CategoryLayout.GRID;
        componentProps.componentUi = CategoryComponentUi.PROFILE_CARD_ITEM;
        break;
      }
      case HomeCategoryUi.POST_FEED: {
        componentProps.filename = 'Container_PostFeed';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/PostFeed.js');
        break;
      }
      case HomeCategoryUi.POST_CARD_GRID: {
        componentProps.filename = 'Container_PostCardCategory';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/PostCardCategory.js');
        break;
      }
      case HomeCategoryUi.STORY_GRID: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.layout = CategoryLayout.GRID;
        componentProps.componentUi = CategoryComponentUi.MESSAGE_CARD;
        break;
      }
      case HomeCategoryUi.STORY_METADATA_CARD: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.shouldShowTitle = true;
        componentProps.shouldShowMoreLink = true;
        componentProps.layout = CategoryLayout.ROW;
        componentProps.componentUi = CategoryComponentUi.PROFILE_CARD_ITEM;
        break;
      }
      case HomeCategoryUi.STORY_METADATA_CARD_GRID: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.layout = CategoryLayout.GRID;
        componentProps.componentUi = CategoryComponentUi.PROFILE_CARD_ITEM;
        break;
      }
      case HomeCategoryUi.MESSAGE_LIST: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.layout = CategoryLayout.LIST;
        componentProps.componentUi = CategoryComponentUi.OUTBOX_MESSAGE;
        componentProps.isImage = true;
        break;
      }
      case HomeCategoryUi.STORY_LIST: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.layout = CategoryLayout.LIST;
        componentProps.componentUi = CategoryComponentUi.OUTBOX_MESSAGE;
        break;
      }
      case HomeCategoryUi.STORY:
      default: {
        componentProps.filename = 'Container_FeedMessages';
        componentProps.importer = /* #__LOADABLE__ */ () =>
          import('../container/FeedMessages.js');
        componentProps.shouldShowTitle = true;
        componentProps.shouldShowMoreLink = true;
        componentProps.layout = CategoryLayout.ROW;
        componentProps.componentUi = CategoryComponentUi.MESSAGE_CARD;
        break;
      }
    }
  }

  return componentProps;
};

export const FeedComponent = props => {
  const {
    livestreamFeed = null,
    category = null,
    titleI18nId = null,
    isLastCategory = false,
    needIntersectionObserver = true,
    intersectionObserverThreshold = 0.1,
  } = props;

  const componentProps = getFeedComponentProps({
    category,
    livestreamFeed,
    titleI18nId,
    isLastCategory,
  });

  if (!componentProps) {
    return null;
  }

  const { ui } = componentProps;
  const minHeight = getPlaceholderHeight(ui, category);

  // Without IntersectionObserver, render directly
  if (!needIntersectionObserver) {
    return (
      <FeedWrapper minHeight={minHeight}>
        <UniversalComponent {...{ ...componentProps, ...props }} />
      </FeedWrapper>
    );
  }

  // With IntersectionObserver for lazy load
  return (
    <WithIntersectionObserver threshold={intersectionObserverThreshold}>
      {({ isIntersected }) => (
        <FeedWrapper minHeight={minHeight}>
          {isIntersected ? (
            <UniversalComponent {...{ ...componentProps, ...props }} />
          ) : (
            <FeedPlaceholder height={minHeight} />
          )}
        </FeedWrapper>
      )}
    </WithIntersectionObserver>
  );
};

const FeedWrapper = styled.div`
  min-height: ${({ minHeight }) => minHeight}px;

  // if empty feed component
  &:has(> :empty) {
    min-height: 0;
  }
`;

FeedComponent.propTypes = {
  category: PropTypes.string,
  titleI18nId: PropTypes.string,
  livestreamFeed: PropTypes.string,
  isLastCategory: PropTypes.bool,
  needIntersectionObserver: PropTypes.bool,
  intersectionObserverThreshold: PropTypes.number,
};

export default FeedComponent;
